/**
 * A sample stores register for OEMA.
 *
 * Three lines carry a manufacturer's own barcode as well as a catalogue
 * number, because a real shelf is a mixture: some bins wear a label the store
 * printed, and some parts arrive with a symbol already on the box. The counter
 * finds a part by either, and the sample has to contain both or that is never
 * demonstrated.
 *
 * Every date is offset from `TODAY` in `./clock`, never written as a literal.
 * This register exists to show what is running out, what is late and what
 * expired last month, and a fixture with typed-in dates stops demonstrating
 * any of that within a fortnight — then shows a clean, well-stocked store for
 * ever. See `src/mock/clock.ts` for what two clocks cost here last time.
 *
 * ── the sample is built to contain the awkward cases ────────────────────
 *
 * A stores sample where everything is in stock proves nothing. This one holds,
 * on purpose:
 *
 *  - a **critical line out of stock with a job waiting on it and nothing on
 *    order** — the CCR regulator card. That is the single worst state a
 *    stores register can be in and the one a screen must never let hide.
 *  - a **part on order for a machine that is down**: the suction fan bearing
 *    for SWP-02, the sweeper the equipment register already shows as awaiting
 *    parts with the note "parts on order, 3 weeks". Until now that note was
 *    the only trace of it anywhere on the platform. Here it is a reserved
 *    line on a dated purchase order, so the sweeper's return can be read
 *    rather than remembered.
 *  - **paint past its shelf life**, which is on the shelf, counted, valued —
 *    and unusable. Stock that cannot be used is not stock.
 *  - a **line ordered and overdue**, because a supplier's promise and a
 *    delivery are different things.
 *  - **lines nobody has ever drawn**, so the screen has to say that their
 *    cover is unknown rather than infinite.
 *  - **lines never counted**, so accuracy is reported over what was counted
 *    and not over what was assumed.
 *  - a **reorder point shorter than its own lead time** — the fence panels.
 *    Ordering on that point is already too late, and the register should say
 *    so rather than raise a requisition that arrives after the gap is cut.
 *
 * Suppliers are named by trade rather than as companies. Inventing supplier
 * names that resemble real Saudi contractors would put fabricated deliveries
 * and fabricated failures against organisations that exist.
 */
import type {
  Movement,
  PurchaseOrder,
  Requisition,
  Reservation,
  StockItem,
  StoresRegister,
} from "../domain/stores";
import { TODAY } from "./clock";

/** A date offset from the sample estate's own clock, as YYYY-MM-DD. */
function day(offset: number): string {
  const d = new Date(TODAY);
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
}

/** A timestamp offset in days, at a plausible working hour. */
function stamp(offset: number, hour = 9): string {
  const d = new Date(TODAY);
  d.setUTCDate(d.getUTCDate() + offset);
  d.setUTCHours(hour, 0, 0, 0);
  return d.toISOString();
}

const MAIN = "MS-1";
const AGL = "SS-AGL";
const TERM = "SS-TERM";
const VAN = "VAN-EL";
const BOND = "BOND";

const STOREKEEPER = "Storekeeper, main store";
const AGL_TECH = "AGL technician";
const FACILITIES = "Facilities supervisor";

let seq = 0;
const id = () => `st-${String(++seq).padStart(3, "0")}`;
let mseq = 0;
const mid = () => `mv-${String(++mseq).padStart(4, "0")}`;

/**
 * A deterministic spread, so the sample is the same on every load and every
 * machine. Issues planted at random would make two screenshots disagree and
 * a test flake once a year.
 */
function spread(count: number, over: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < count; i += 1) {
    /*
     * A fixed irrational step keeps the gaps uneven without a random source.
     * The phase starts at one step rather than at zero: from zero, the first
     * draw of EVERY line lands exactly one day ago, which reads on the screen
     * as an airport that issued twenty-eight different things yesterday.
     */
    const f = ((i + 1) * 0.618_033_988_75) % 1;
    out.push(-Math.round(f * over) - 1);
  }
  return out.sort((a, b) => a - b);
}

interface Draw {
  /** how many separate issues in the last year */
  times: number;
  /** how many units each time */
  qty: number;
  /** who drew them and which job, where it was a job */
  by: string;
  workOrders?: string[];
  /**
   * Days ago, given explicitly, where the line's note tells a story the spread
   * would contradict. A sample whose prose and whose dates disagree is worse
   * than one with neither.
   */
  on?: number[];
}

const movements: Movement[] = [];

/** Plant a year of issues for one line, oldest first. */
function draws(sku: string, location: string, d: Draw): void {
  const days = d.on ?? spread(d.times, 360);
  days.forEach((offset, i) => {
    movements.push({
      id: mid(),
      at: stamp(offset, 7 + (i % 6)),
      sku,
      kind: "issue",
      qty: -d.qty,
      location,
      by: d.by,
      workOrder: d.workOrders?.[i % d.workOrders.length],
    });
  });
}

/** A receipt against a purchase order, so the shelf has a history and a cost. */
function receipt(sku: string, location: string, offset: number, qty: number, order: string, unitCost?: number): void {
  movements.push({
    id: mid(),
    at: stamp(offset, 11),
    sku,
    kind: "receipt",
    qty,
    location,
    by: STOREKEEPER,
    reference: order,
    unitCost,
  });
}

export function mockStores(): StoresRegister {
  seq = 0;
  mseq = 0;
  movements.length = 0;

  const items: StockItem[] = [
    /* ── electrical: the airfield's own spares ─────────────────────────── */
    {
      id: id(),
      sku: "EL-1001",
      name: "Runway edge lamp, 6.6 A, 200 W halogen",
      category: "agl",
      uom: "each",
      unitCost: 385,
      manufacturer: "ADB SAFEGATE",
      barcode: "5901234123457",
      partNumber: "RWY-EL-200",
      min: 40,
      max: 120,
      leadDays: 28,
      critical: { why: "an unlit edge segment closes the runway at night" },
      serves: { assetTypes: ["runway_light"], trades: ["electrical"] },
      supplier: "Airfield lighting supplier",
      contractRef: "CTR-2024-018",
      bins: [
        { location: AGL, bin: "A-01", onHand: 64 },
        { location: MAIN, bin: "E-11", onHand: 12 },
      ],
      counted: { at: day(-46), by: AGL_TECH, variance: 0 },
    },
    {
      id: id(),
      sku: "EL-1002",
      name: "Isolating transformer, 6.6 A, 45 W",
      category: "agl",
      uom: "each",
      unitCost: 640,
      manufacturer: "ADB SAFEGATE",
      partNumber: "IT-45",
      min: 24,
      max: 60,
      leadDays: 35,
      critical: { why: "a failed transformer takes its light out of the circuit" },
      serves: { assetTypes: ["runway_light", "taxiway_light"], trades: ["electrical"] },
      supplier: "Airfield lighting supplier",
      contractRef: "CTR-2024-018",
      bins: [{ location: AGL, bin: "A-04", onHand: 11 }],
      counted: { at: day(-46), by: AGL_TECH, variance: -2 },
    },
    {
      id: id(),
      sku: "EL-1003",
      name: "Constant current regulator control card, 30 kVA",
      category: "agl",
      uom: "each",
      unitCost: 11_400,
      manufacturer: "ADB SAFEGATE",
      partNumber: "CCR-CTRL-30",
      min: 2,
      max: 4,
      leadDays: 63,
      critical: { why: "a dead regulator takes a whole lighting circuit out; there is no local repair" },
      serves: { assetTypes: ["ccr"], trades: ["electrical"] },
      supplier: "Airfield lighting supplier",
      contractRef: "CTR-2024-018",
      bins: [{ location: AGL, bin: "A-09", onHand: 0 }],
      counted: { at: day(-46), by: AGL_TECH, variance: 0 },
      note: "The spare was fitted to CCR-03 in the June failure and never replaced.",
    },
    {
      id: id(),
      sku: "EL-1010",
      name: "Cable, 5 kV single core, 6 mm²",
      category: "agl",
      uom: "m",
      unitCost: 46,
      min: 300,
      max: 1000,
      leadDays: 21,
      serves: { assetTypes: ["taxiway_light"], trades: ["electrical"] },
      supplier: "Airfield lighting supplier",
      bins: [{ location: AGL, bin: "A-12", onHand: 740 }],
      counted: { at: day(-46), by: AGL_TECH, variance: 0 },
    },
    {
      id: id(),
      sku: "EL-1020",
      name: "Moulded case circuit breaker, 250 A",
      category: "electrical",
      uom: "each",
      unitCost: 2150,
      min: 3,
      max: 8,
      leadDays: 30,
      serves: { trades: ["electrical"] },
      supplier: "Electrical distributor",
      bins: [{ location: MAIN, bin: "E-02", onHand: 5 }],
    },

    /* ── equipment and plant: what keeps the fleet running ─────────────── */
    {
      id: id(),
      sku: "EQ-2001",
      name: "Suction fan bearing set, CityCat VR50",
      category: "equipment",
      uom: "set",
      unitCost: 4300,
      manufacturer: "Bucher",
      partNumber: "VR50-FAN-BRG",
      min: 1,
      max: 2,
      leadDays: 21,
      critical: { why: "the runway sweepers are the only means of clearing FOD at scale" },
      serves: { fleetKinds: ["sweeper"] },
      supplier: "Plant parts supplier",
      bins: [{ location: MAIN, bin: "P-03", onHand: 0 }],
      note: "Fitted to SWP-01 in March. SWP-02 has been down since the second failure.",
    },
    {
      id: id(),
      sku: "EQ-2002",
      name: "Sweeper main brush, poly/steel mix",
      category: "equipment",
      uom: "each",
      unitCost: 1850,
      min: 4,
      max: 10,
      leadDays: 24,
      serves: { fleetKinds: ["sweeper"] },
      supplier: "Plant parts supplier",
      bins: [{ location: MAIN, bin: "P-01", onHand: 7 }],
      counted: { at: day(-88), by: STOREKEEPER, variance: 1 },
    },
    {
      id: id(),
      sku: "EQ-2003",
      name: "Hydraulic hose, 3/4 in, 2 m, crimped",
      category: "equipment",
      uom: "each",
      unitCost: 420,
      min: 6,
      max: 16,
      leadDays: 7,
      serves: { fleetKinds: ["sweeper", "earthmoving", "access", "haulage"] },
      supplier: "Plant parts supplier",
      bins: [{ location: MAIN, bin: "P-06", onHand: 9 }],
      counted: { at: day(-88), by: STOREKEEPER, variance: 0 },
    },
    {
      id: id(),
      sku: "EQ-2010",
      name: "Engine oil filter, heavy plant",
      category: "equipment",
      uom: "each",
      unitCost: 145,
      barcode: "4006381333931",
      min: 12,
      max: 30,
      leadDays: 10,
      serves: { fleetKinds: ["sweeper", "earthmoving", "haulage", "power"] },
      supplier: "Plant parts supplier",
      bins: [{ location: MAIN, bin: "P-08", onHand: 21 }],
      counted: { at: day(-88), by: STOREKEEPER, variance: 0 },
    },

    /* ── paints and markings: the shelf life category ──────────────────── */
    {
      id: id(),
      sku: "PT-3001",
      name: "Airfield marking paint, white, waterborne",
      category: "paint",
      uom: "drum",
      unitCost: 1250,
      min: 8,
      max: 24,
      leadDays: 18,
      serves: { assetTypes: ["marking"], trades: ["civil"] },
      supplier: "Marking materials supplier",
      hazardous: true,
      expiresAt: day(-23),
      bins: [{ location: MAIN, bin: "H-01", onHand: 6 }],
      counted: { at: day(-120), by: STOREKEEPER, variance: 0 },
      note: "Batch received before the marking season was deferred; shelf life ran out on the shelf.",
    },
    {
      id: id(),
      sku: "PT-3002",
      name: "Glass beads, type III, drop-on",
      category: "paint",
      uom: "bag",
      unitCost: 210,
      min: 20,
      max: 60,
      leadDays: 18,
      serves: { assetTypes: ["marking"], trades: ["civil"] },
      supplier: "Marking materials supplier",
      bins: [{ location: MAIN, bin: "H-04", onHand: 44 }],
      counted: { at: day(-120), by: STOREKEEPER, variance: 0 },
    },

    /* ── civil ─────────────────────────────────────────────────────────── */
    {
      id: id(),
      sku: "CV-4001",
      name: "Joint sealant, hot-poured, airfield grade",
      category: "civil",
      uom: "box",
      unitCost: 390,
      min: 15,
      max: 40,
      leadDays: 25,
      serves: { assetTypes: ["pavement"], trades: ["civil"] },
      supplier: "Pavement materials supplier",
      hazardous: true,
      expiresAt: day(214),
      bins: [{ location: MAIN, bin: "C-02", onHand: 12 }],
      counted: { at: day(-120), by: STOREKEEPER, variance: 0 },
    },
    {
      id: id(),
      sku: "CV-4002",
      name: "Cold-mix asphalt, 25 kg bag",
      category: "civil",
      uom: "bag",
      unitCost: 55,
      min: 40,
      max: 120,
      leadDays: 5,
      serves: { assetTypes: ["pavement"], trades: ["civil"] },
      supplier: "Pavement materials supplier",
      bins: [{ location: MAIN, bin: "C-05", onHand: 86 }],
    },

    /* ── utilities ─────────────────────────────────────────────────────── */
    {
      id: id(),
      sku: "UT-5001",
      name: "Mechanical seal, chilled water pump, 65 mm",
      category: "mechanical",
      uom: "each",
      unitCost: 2750,
      min: 2,
      max: 4,
      leadDays: 30,
      critical: { why: "a failed seal takes a chilled water pump out and the terminal warms within hours" },
      serves: { trades: ["utilities", "electromech"] },
      supplier: "Mechanical services supplier",
      bins: [{ location: MAIN, bin: "U-01", onHand: 2 }],
      counted: { at: day(-60), by: FACILITIES, variance: 0 },
    },
    {
      id: id(),
      sku: "UT-5002",
      name: "Chiller filter set, primary",
      category: "utilities",
      uom: "set",
      unitCost: 980,
      min: 4,
      max: 12,
      leadDays: 20,
      serves: { trades: ["utilities"] },
      supplier: "Mechanical services supplier",
      bins: [{ location: MAIN, bin: "U-03", onHand: 6 }],
      counted: { at: day(-60), by: FACILITIES, variance: 0 },
    },

    /* ── BHS ───────────────────────────────────────────────────────────── */
    {
      id: id(),
      sku: "BH-6001",
      name: "Conveyor belt section, 1200 mm, cleated",
      category: "bhs",
      uom: "m",
      unitCost: 1450,
      min: 10,
      max: 30,
      leadDays: 45,
      critical: { why: "a torn belt stops a check-in line and baggage backs up into the hall" },
      serves: { trades: ["electromech"] },
      supplier: "Baggage systems supplier",
      contractRef: "CTR-2023-041",
      bins: [{ location: TERM, bin: "B-01", onHand: 18 }],
      counted: { at: day(-30), by: FACILITIES, variance: 0 },
    },
    {
      id: id(),
      sku: "BH-6002",
      name: "Photo-eye sensor, through-beam",
      category: "bhs",
      uom: "each",
      unitCost: 310,
      min: 8,
      max: 20,
      leadDays: 14,
      serves: { trades: ["electromech"] },
      supplier: "Baggage systems supplier",
      contractRef: "CTR-2023-041",
      bins: [{ location: TERM, bin: "B-04", onHand: 8 }],
      counted: { at: day(-30), by: FACILITIES, variance: 0 },
    },

    /* ── fencing and perimeter ─────────────────────────────────────────── */
    {
      id: id(),
      sku: "FN-7001",
      name: "Perimeter fence panel, 3 m, galvanised",
      category: "fencing",
      uom: "each",
      unitCost: 720,
      min: 6,
      max: 20,
      /*
       * A lead time longer than the cover this reorder point buys. The
       * register is meant to notice that and say so — see arrivesInTime().
       */
      leadDays: 40,
      critical: { why: "a breached perimeter is a security finding the same day" },
      serves: { trades: ["civil"] },
      supplier: "Perimeter contractor",
      bins: [{ location: MAIN, bin: "F-01", onHand: 4 }],
      counted: { at: day(-150), by: STOREKEEPER, variance: 0 },
    },
    {
      id: id(),
      sku: "FN-7002",
      name: "Razor tape coil, 450 mm",
      category: "fencing",
      uom: "coil",
      unitCost: 240,
      min: 10,
      max: 30,
      leadDays: 12,
      serves: { trades: ["civil"] },
      supplier: "Perimeter contractor",
      bins: [{ location: MAIN, bin: "F-03", onHand: 22 }],
    },

    /* ── electro-mechanical ────────────────────────────────────────────── */
    {
      id: id(),
      sku: "EM-8001",
      name: "Contactor, 3-pole, 95 A",
      category: "electromech",
      uom: "each",
      unitCost: 580,
      min: 6,
      max: 15,
      leadDays: 16,
      serves: { trades: ["electromech", "electrical"] },
      supplier: "Electrical distributor",
      bins: [
        { location: MAIN, bin: "M-02", onHand: 7 },
        { location: VAN, onHand: 2 },
      ],
      counted: { at: day(-75), by: FACILITIES, variance: -1 },
    },
    {
      id: id(),
      sku: "EM-8002",
      name: "Door actuator, sliding terminal door",
      category: "electromech",
      uom: "each",
      unitCost: 3400,
      min: 2,
      max: 5,
      leadDays: 28,
      serves: { trades: ["electromech"] },
      supplier: "Door systems supplier",
      bins: [{ location: TERM, bin: "B-09", onHand: 1 }],
      counted: { at: day(-30), by: FACILITIES, variance: 0 },
    },

    /* ── buildings ─────────────────────────────────────────────────────── */
    {
      id: id(),
      sku: "BD-9001",
      name: "Ceiling tile, 600 × 600, mineral fibre",
      category: "building",
      uom: "each",
      unitCost: 38,
      min: 100,
      max: 400,
      leadDays: 10,
      serves: { trades: ["civil"] },
      supplier: "Building materials supplier",
      bins: [{ location: TERM, bin: "B-12", onHand: 260 }],
      counted: { at: day(-30), by: FACILITIES, variance: 4 },
    },
    {
      id: id(),
      sku: "BD-9002",
      name: "Door closer, heavy duty",
      category: "building",
      uom: "each",
      unitCost: 290,
      min: 6,
      max: 18,
      leadDays: 12,
      serves: { trades: ["civil"] },
      supplier: "Building materials supplier",
      bins: [{ location: TERM, bin: "B-14", onHand: 11 }],
    },

    /* ── consumables ───────────────────────────────────────────────────── */
    {
      id: id(),
      sku: "CN-0101",
      name: "Nitrile gloves, box of 100",
      category: "consumable",
      uom: "box",
      unitCost: 32,
      barcode: "5000112637922",
      min: 40,
      max: 150,
      leadDays: 4,
      supplier: "General supplies",
      bins: [{ location: MAIN, bin: "G-01", onHand: 96 }],
      counted: { at: day(-20), by: STOREKEEPER, variance: -3 },
    },
    {
      id: id(),
      sku: "CN-0102",
      name: "Absorbent granules, 20 kg sack",
      category: "consumable",
      uom: "sack",
      unitCost: 78,
      min: 12,
      max: 40,
      leadDays: 6,
      serves: { trades: ["cleaning"] },
      supplier: "General supplies",
      bins: [{ location: MAIN, bin: "G-04", onHand: 15 }],
    },
    {
      id: id(),
      sku: "CN-0103",
      name: "Cable ties, 300 mm, UV stable, bag of 100",
      category: "consumable",
      uom: "bag",
      unitCost: 26,
      min: 20,
      max: 60,
      leadDays: 4,
      supplier: "General supplies",
      bins: [{ location: MAIN, bin: "G-07", onHand: 41 }],
    },

    /* ── tools: where the money stands still ───────────────────────────── */
    {
      id: id(),
      sku: "TL-0201",
      name: "Torque wrench, 40–200 Nm, calibrated",
      category: "tools",
      uom: "each",
      unitCost: 1650,
      min: 1,
      max: 3,
      leadDays: 30,
      serves: { trades: ["electromech"] },
      supplier: "Tool supplier",
      bins: [{ location: BOND, bin: "T-01", onHand: 3 }],
      note: "Calibration due with the tool, not with the stores. Nothing drawn in a year.",
    },
    {
      id: id(),
      sku: "TL-0202",
      name: "Insulation resistance tester, 5 kV",
      category: "tools",
      uom: "each",
      unitCost: 8900,
      min: 1,
      max: 2,
      leadDays: 45,
      serves: { trades: ["electrical"] },
      supplier: "Tool supplier",
      bins: [{ location: BOND, bin: "T-03", onHand: 1 }],
    },
  ];

  /* ── a year of drawing down ───────────────────────────────────────────
   *
   * Planted as issues so consumption, cover and the fill rate are computed
   * from movements rather than declared. A figure a fixture states is a
   * figure the code never has to get right.
   */
  draws("EL-1001", AGL, { times: 26, qty: 3, by: AGL_TECH, workOrders: ["WO-000188", "WO-000204", "WO-000219"] });
  draws("EL-1002", AGL, { times: 14, qty: 2, by: AGL_TECH, workOrders: ["WO-000188", "WO-000221"] });
  /* the note on this line says the spare was fitted in the June failure */
  draws("EL-1003", AGL, { times: 1, qty: 1, by: AGL_TECH, workOrders: ["WO-000167"], on: [-96] });
  draws("EL-1010", AGL, { times: 6, qty: 60, by: AGL_TECH });
  draws("EL-1020", MAIN, { times: 3, qty: 1, by: FACILITIES });
  /* and this one that the bearing went into SWP-01 in March */
  draws("EQ-2001", MAIN, { times: 1, qty: 1, by: STOREKEEPER, workOrders: ["WO-000190"], on: [-190] });
  draws("EQ-2002", MAIN, { times: 9, qty: 1, by: STOREKEEPER, workOrders: ["WO-000201"] });
  draws("EQ-2003", MAIN, { times: 12, qty: 1, by: STOREKEEPER });
  draws("EQ-2010", MAIN, { times: 18, qty: 2, by: STOREKEEPER });
  draws("PT-3001", MAIN, { times: 4, qty: 5, by: FACILITIES, workOrders: ["WO-000175"] });
  draws("PT-3002", MAIN, { times: 4, qty: 12, by: FACILITIES, workOrders: ["WO-000175"] });
  draws("CV-4001", MAIN, { times: 7, qty: 6, by: FACILITIES, workOrders: ["WO-000199"] });
  draws("CV-4002", MAIN, { times: 11, qty: 12, by: FACILITIES });
  draws("UT-5001", MAIN, { times: 2, qty: 1, by: FACILITIES, workOrders: ["WO-000209"] });
  draws("UT-5002", MAIN, { times: 5, qty: 2, by: FACILITIES });
  draws("BH-6001", TERM, { times: 3, qty: 4, by: FACILITIES, workOrders: ["WO-000212"] });
  draws("BH-6002", TERM, { times: 10, qty: 2, by: FACILITIES });
  draws("FN-7001", MAIN, { times: 5, qty: 3, by: FACILITIES, workOrders: ["WO-000196"] });
  draws("FN-7002", MAIN, { times: 4, qty: 4, by: FACILITIES });
  draws("EM-8001", MAIN, { times: 8, qty: 2, by: FACILITIES });
  draws("EM-8002", TERM, { times: 3, qty: 1, by: FACILITIES, workOrders: ["WO-000216"] });
  draws("BD-9001", TERM, { times: 16, qty: 20, by: FACILITIES });
  draws("BD-9002", TERM, { times: 6, qty: 2, by: FACILITIES });
  draws("CN-0101", MAIN, { times: 34, qty: 6, by: STOREKEEPER });
  draws("CN-0102", MAIN, { times: 9, qty: 3, by: STOREKEEPER });
  draws("CN-0103", MAIN, { times: 15, qty: 4, by: STOREKEEPER });
  /* TL-0201 is drawn on purpose by nobody: it is the obsolete line */
  draws("TL-0202", BOND, { times: 2, qty: 1, by: AGL_TECH });

  /* what came in, so the shelf has a provenance and a cost */
  receipt("EL-1001", AGL, -142, 80, "PO-2026-0071", 385);
  receipt("EL-1002", AGL, -142, 30, "PO-2026-0071", 640);
  receipt("EQ-2002", MAIN, -96, 8, "PO-2026-0084", 1850);
  receipt("CV-4002", MAIN, -54, 100, "PO-2026-0102", 55);
  receipt("PT-3001", MAIN, -196, 20, "PO-2026-0058", 1250);
  receipt("BD-9001", TERM, -70, 300, "PO-2026-0106", 38);
  receipt("CN-0101", MAIN, -33, 120, "PO-2026-0118", 32);

  /*
   * The issues the reservations say were made.
   *
   * Four reservations carry `issued`, and without these the register would
   * claim stock had gone to a job with no movement recording it leaving —
   * which is precisely the state `partsTimeline` exists to make visible, and
   * a sample that contains it by accident teaches the wrong thing.
   */
  movements.push(
    { id: mid(), at: stamp(-3, 10), sku: "EL-1001", kind: "issue", qty: -12, location: AGL, by: AGL_TECH, workOrder: "WO-000005" },
    { id: mid(), at: stamp(-5, 11), sku: "UT-5001", kind: "issue", qty: -1, location: MAIN, by: FACILITIES, workOrder: "WO-000230" },
    { id: mid(), at: stamp(-14, 9), sku: "EM-8002", kind: "issue", qty: -1, location: TERM, by: FACILITIES, workOrder: "WO-000228" },
    { id: mid(), at: stamp(-1, 15), sku: "BH-6002", kind: "issue", qty: -2, location: TERM, by: FACILITIES, workOrder: "WO-000234" },
  );

  /* a count that found a difference, recorded as its own movement */
  movements.push({
    id: mid(),
    at: stamp(-46, 13),
    sku: "EL-1002",
    kind: "count",
    qty: -2,
    location: AGL,
    bin: "A-04",
    by: AGL_TECH,
    reference: "CNT-2026-09",
    note: "Two transformers fitted during the July circuit fault were never booked out.",
  });

  /* ── what is promised to open jobs ────────────────────────────────────
   *
   * Each of these is a real reservation against a work order that exists in
   * the sample estate, which is what makes "available" differ from "on hand"
   * on the screen rather than only in the tests.
   */
  /*
   * Every reservation still outstanding names a work order that EXISTS in the
   * sample estate, so the parts card appears on a real work order page and the
   * numbers on this desk can be followed. The sweeper's is the exception and
   * deliberately so: WO-000231 is the number the equipment register's own
   * maintenance history already carries for it.
   */
  const reservations: Reservation[] = [
    {
      id: "rs-001",
      sku: "EQ-2001",
      qty: 1,
      /* the sweeper the equipment register shows as awaiting parts */
      workOrder: "WO-000231",
      at: stamp(-9, 10),
      by: "Plant workshop",
      neededAt: day(-2),
      issued: 0,
    },
    {
      id: "rs-002",
      sku: "EL-1003",
      qty: 1,
      /* the intermittent circuit fault: a regulator card, and there is none */
      workOrder: "WO-000009",
      at: stamp(-12, 8),
      by: AGL_TECH,
      neededAt: day(1),
      issued: 0,
    },
    {
      id: "rs-003",
      sku: "EL-1002",
      qty: 6,
      /* two holding position lights out at B1 */
      workOrder: "WO-000005",
      at: stamp(-4, 8),
      by: AGL_TECH,
      neededAt: day(3),
      issued: 0,
    },
    {
      id: "rs-004",
      sku: "EL-1001",
      qty: 12,
      workOrder: "WO-000005",
      at: stamp(-4, 8),
      by: AGL_TECH,
      neededAt: day(3),
      issued: 12,
    },
    {
      id: "rs-005",
      sku: "FN-7001",
      qty: 3,
      /* the annual VOR compound fence inspection */
      workOrder: "WO-000013",
      at: stamp(-2, 9),
      by: FACILITIES,
      neededAt: day(6),
      issued: 0,
    },
    {
      id: "rs-006",
      sku: "UT-5001",
      qty: 1,
      workOrder: "WO-000230",
      at: stamp(-6, 9),
      by: FACILITIES,
      neededAt: day(9),
      issued: 1,
    },
    {
      id: "rs-007",
      sku: "EM-8002",
      qty: 1,
      workOrder: "WO-000228",
      at: stamp(-15, 9),
      by: FACILITIES,
      neededAt: day(-5),
      issued: 1,
    },
    {
      id: "rs-008",
      sku: "BH-6002",
      qty: 2,
      workOrder: "WO-000234",
      at: stamp(-1, 14),
      by: FACILITIES,
      neededAt: day(4),
      /* collected: the terminal's own work orders are not in this sample, so
       * an outstanding line against one would put an unfollowable number on
       * the desk */
      issued: 2,
    },
    {
      id: "rs-009",
      sku: "CV-4001",
      qty: 18,
      /* "On hold until sealant delivery" — the work order says so itself */
      workOrder: "WO-000010",
      at: stamp(-7, 8),
      by: FACILITIES,
      neededAt: day(2),
      issued: 0,
    },
    {
      id: "rs-010",
      sku: "PT-3001",
      qty: 5,
      /*
       * A repaint that is IN PROGRESS, against paint that expired on the
       * shelf. In progress on purpose: on_hold is reachable only from
       * dispatched or in_progress, so a job short of a part in any other
       * status cannot be held at all — and a sample where nothing short can
       * be held cannot demonstrate the hold that stops the clock.
       */
      workOrder: "WO-000001",
      at: stamp(-5, 8),
      by: FACILITIES,
      neededAt: day(8),
      issued: 0,
    },
  ];

  /* ── what is on order ─────────────────────────────────────────────────── */
  const orders: PurchaseOrder[] = [
    {
      id: "po-001",
      number: "PO-2026-0131",
      supplier: "Plant parts supplier",
      raisedAt: stamp(-8, 10),
      raisedBy: "Procurement officer",
      expectedAt: day(13),
      status: "open",
      requisitionNumber: "RQ-2026-0207",
      currency: "SAR",
      lines: [{ sku: "EQ-2001", qty: 2, unitCost: 4300, received: 0 }],
      receipts: [],
    },
    {
      id: "po-002",
      number: "PO-2026-0129",
      supplier: "Airfield lighting supplier",
      contractRef: "CTR-2024-018",
      raisedAt: stamp(-31, 10),
      raisedBy: "Procurement officer",
      /* promised before today and not here: an overdue order, stated as such */
      expectedAt: day(-4),
      status: "part_received",
      requisitionNumber: "RQ-2026-0201",
      currency: "SAR",
      lines: [
        { sku: "EL-1002", qty: 30, unitCost: 640, received: 12 },
        { sku: "EL-1010", qty: 500, unitCost: 46, received: 500 },
      ],
      receipts: [
        {
          at: stamp(-11, 11),
          by: STOREKEEPER,
          deliveryNote: "DN-77412",
          lines: [
            { sku: "EL-1002", qty: 12, location: AGL, bin: "A-04" },
            { sku: "EL-1010", qty: 500, location: AGL, bin: "A-12" },
          ],
          rejected: [{ sku: "EL-1002", qty: 2, why: "Cases split in transit; potting compound cracked." }],
        },
      ],
    },
    {
      id: "po-004",
      number: "PO-2026-0135",
      supplier: "Pavement materials supplier",
      raisedAt: stamp(-6, 10),
      raisedBy: "Procurement officer",
      expectedAt: day(4),
      status: "open",
      currency: "SAR",
      lines: [{ sku: "CV-4001", qty: 30, unitCost: 390, received: 0 }],
      receipts: [],
    },
    {
      id: "po-003",
      number: "PO-2026-0133",
      supplier: "Marking materials supplier",
      raisedAt: stamp(-3, 10),
      raisedBy: "Procurement officer",
      expectedAt: day(15),
      status: "open",
      currency: "SAR",
      lines: [{ sku: "PT-3001", qty: 24, unitCost: 1250, received: 0 }],
      receipts: [],
    },
  ];

  /* the two receipts above are movements as well — the shelf and the order agree */
  receipt("EL-1002", AGL, -11, 12, "PO-2026-0129", 640);
  receipt("EL-1010", AGL, -11, 500, "PO-2026-0129", 46);

  /* ── what is asking to be bought ──────────────────────────────────────── */
  const requisitions: Requisition[] = [
    {
      id: "rq-001",
      number: "RQ-2026-0211",
      raisedAt: stamp(-5, 9),
      raisedBy: AGL_TECH,
      department: "Airfield Electrical",
      status: "submitted",
      lines: [
        {
          sku: "EL-1003",
          qty: 2,
          why: "No regulator card on the shelf; CCR-03 is running without a spare.",
          workOrder: "WO-000226",
          estimatedUnitCost: 11_400,
        },
      ],
      approvals: [],
      note: "Raised the day the reservation could not be filled.",
    },
    {
      id: "rq-002",
      number: "RQ-2026-0209",
      raisedAt: stamp(-13, 9),
      raisedBy: FACILITIES,
      department: "Facilities",
      status: "approved",
      lines: [
        { sku: "UT-5002", qty: 6, why: "Replenishment to maximum ahead of the summer load.", estimatedUnitCost: 980 },
      ],
      approvals: [
        {
          at: stamp(-11, 15),
          by: "Maintenance Manager",
          role: "Maintenance Manager",
          decision: "approved",
          limit: 50_000,
        },
      ],
    },
    {
      id: "rq-003",
      number: "RQ-2026-0207",
      raisedAt: stamp(-10, 9),
      raisedBy: "Plant workshop",
      department: "Airside Operations",
      status: "ordered",
      orderNumber: "PO-2026-0131",
      lines: [
        {
          sku: "EQ-2001",
          qty: 2,
          why: "SWP-02 down; one to fit and one to hold as the spare that was used in March.",
          workOrder: "WO-000231",
          estimatedUnitCost: 4300,
        },
      ],
      approvals: [
        { at: stamp(-9, 11), by: "Maintenance Manager", role: "Maintenance Manager", decision: "approved", limit: 50_000 },
      ],
    },
  ];

  return {
    currency: "SAR",
    locations: [
      { code: MAIN, name: "Main engineering store", kind: "main", custodian: "Engineering" },
      { code: AGL, name: "AGL substation store", kind: "satellite", custodian: "Airfield Electrical" },
      { code: TERM, name: "Terminal store", kind: "satellite", custodian: "Facilities" },
      { code: VAN, name: "Electrical van stock", kind: "van", custodian: "Airfield Electrical" },
      { code: BOND, name: "Calibrated and bonded store", kind: "bonded", custodian: "Engineering" },
    ],
    items,
    movements: movements.slice().sort((a, b) => a.at.localeCompare(b.at)),
    reservations,
    requisitions,
    orders,
  };
}
